/**
 * Las reglas de `parseGridQuery` de la API (lista permitida y forma de la consulta), para que una
 * consulta que la API rechazaría con 400 también falle en modo local: así una pantalla pasa de local
 * a remoto sin cambiar su conducta. Los mensajes son los de la API.
 */
import type { GridFieldSpec, GridFilter, GridQuery, GridSpec } from './grid-query';

export const MAX_SEARCH_LENGTH = 100;
const MAX_VALUE_LENGTH = 500;
const MAX_VALUES = 500;
const MAX_FILTERS = 50;
const MAX_SORT = 10;
const MAX_LEVELS = 10;

/** Operadores por tipo de campo (los mismos que la API). */
const OPS_BY_TYPE: Record<string, string[]> = {
  string: ['in', 'eq', 'contains'],
  number: ['in', 'eq', 'range'],
  date: ['in', 'eq', 'range'],
  boolean: ['in', 'eq'],
};

/** Fecha y hora con su desfase (`Z` o `±hh:mm`), como la exige la API. */
const WITH_OFFSET = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** Una consulta que la API respondería con 400. */
export class GridQueryError extends Error {
  override readonly name = 'GridQueryError';
}

const fail = (message: string): never => {
  throw new GridQueryError(message);
};

type Capability = 'sort' | 'filter' | 'group' | 'search' | 'facet';

function allowedField(spec: GridSpec, field: string, capability: Capability, action: string) {
  if (!Object.prototype.hasOwnProperty.call(spec.fields, field))
    fail(`El campo «${field}» no existe en esta grilla`);
  const definition = spec.fields[field];
  if (!definition[capability]) fail(`No se puede ${action} «${field}»`);
  return definition;
}

function checkValue(value: unknown, definition: GridFieldSpec, field: string): void {
  if (value === null) return;
  const valid =
    definition.type === 'number'
      ? typeof value === 'number' && Number.isFinite(value)
      : definition.type === 'boolean'
        ? typeof value === 'boolean'
        : definition.type === 'date'
          ? typeof value === 'string' &&
            WITH_OFFSET.test(value) &&
            !Number.isNaN(new Date(value).getTime())
          : typeof value === 'string' && value.length <= MAX_VALUE_LENGTH;
  if (!valid) fail(`Valor no válido para «${field}»`);
}

function checkFilter({ field, op, values }: GridFilter, spec: GridSpec): void {
  const definition = allowedField(spec, field, 'filter', 'filtrar por');
  if (!(OPS_BY_TYPE[definition.type] ?? OPS_BY_TYPE['string']).includes(op))
    fail(`El filtro «${op}» no se puede usar en «${field}»`);
  if (!Array.isArray(values) || values.length > MAX_VALUES) fail(`Valor no válido para «${field}»`);
  if (op === 'eq' && values.length !== 1) fail(`El filtro «eq» de «${field}» lleva un solo valor`);
  if (op === 'range' && values.length !== 2)
    fail(`El filtro «range» de «${field}» lleva dos valores: desde y hasta`);
  if (op === 'contains') {
    const [text] = values;
    if (values.length !== 1 || typeof text !== 'string') fail(`Valor no válido para «${field}»`);
    if ((text as string).length > MAX_SEARCH_LENGTH)
      fail(`El filtro «contains» de «${field}» admite como máximo ${MAX_SEARCH_LENGTH} caracteres`);
    return;
  }
  values.forEach((value) => checkValue(value, definition, field));
}

/** Lanza `GridQueryError` con el mensaje de la API si la consulta no cabe en `spec`. */
export function validateGridQuery(query: GridQuery, spec: GridSpec): void {
  if (!Number.isInteger(query.page) || query.page < 1) fail('La página debe ser un entero desde 1');
  if (!Number.isInteger(query.pageSize) || query.pageSize < 0)
    fail('El tamaño de página debe ser un entero desde 0');

  if (query.sort.length > MAX_SORT) fail(`El orden admite como máximo ${MAX_SORT} columnas`);
  for (const { field, dir } of query.sort) {
    allowedField(spec, field, 'sort', 'ordenar por');
    if (dir !== 'asc' && dir !== 'desc') fail(`La dirección de «${field}» debe ser asc o desc`);
  }

  if (query.filters.length > MAX_FILTERS) fail(`Se admiten como máximo ${MAX_FILTERS} filtros`);
  query.filters.forEach((filter) => checkFilter(filter, spec));

  const search = (query.search ?? '').trim();
  if (search.length > MAX_SEARCH_LENGTH)
    fail(`«search» admite como máximo ${MAX_SEARCH_LENGTH} caracteres`);
  if (search && !Object.values(spec.fields).some((definition) => definition.search))
    fail('Esta grilla no admite búsqueda');

  if (query.groupBy.length > MAX_LEVELS)
    fail(`Se admiten como máximo ${MAX_LEVELS} niveles de agrupación`);
  const levels = query.groupBy.map((field) => allowedField(spec, field, 'group', 'agrupar por'));
  query.groupBy.forEach((field, index) => {
    if (query.groupBy.indexOf(field) !== index) fail(`«${field}» se repite en groupBy`);
  });
  const groupPath = query.groupPath ?? [];
  if (groupPath.length > query.groupBy.length)
    fail('groupPath trae más claves que niveles de groupBy');
  groupPath.forEach((key, index) => checkValue(key, levels[index], query.groupBy[index]));
  if (query.view === 'groups' && groupPath.length >= query.groupBy.length)
    fail('Para la vista groups no hay un nivel de agrupación que abrir');

  if (query.view === 'facet') {
    if (!query.facetField) fail('Falta «facetField» para la vista facet');
    else allowedField(spec, query.facetField, 'facet', 'pedir los valores de');
  }
}
